import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useUser } from '../contexts/UserContext';
import { bookingAPI, userAPI } from '../services/api';
import { useI18n } from '../i18n/i18nContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { getTranslatedField } from '../utils/translationUtils';
import { Booking } from '@shared/types';
import { formatInTimeZone } from 'date-fns-tz';
import { ru as ruLocale, et as etLocale, fi as fiLocale } from 'date-fns/locale';
import { pushToDataLayer, clearEcommerce } from '../utils/dataLayer';
import { formatPrice } from '@shared/utils';

const TALLINN_TIMEZONE = 'Europe/Tallinn';

interface LocationState {
  guestBooking?: boolean;
  bookingCount?: number;
  service?: {
    id: string;
    name: string;
    name_et?: string;
    duration: number;
    price?: number;
    currency?: string;
  };
  date?: string;
  time?: string;
}

const BookingSuccessPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const location = useLocation();
  const state = location.state as LocationState | undefined;
  const { isAuthenticated } = useAuth0();
  const { user } = useUser();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t, language } = useI18n();
  useDocumentTitle(t.booking.success);
  const hasTrackedPurchase = useRef(false);
  const [userPhone, setUserPhone] = useState<string>('');

  useEffect(() => {
    const fetchBookings = async () => {
      console.log('[BookingSuccessPage] orderId:', orderId);
      console.log('[BookingSuccessPage] state:', state);

      // Handle guest bookings without orderId - use state data
      if (state?.guestBooking && !orderId) {
        console.log('[BookingSuccessPage] Using state data for guest booking without orderId');
        setLoading(false);
        return;
      }

      if (!orderId) {
        console.log('[BookingSuccessPage] No orderId found, showing error');
        setError(t.errors.general);
        setLoading(false);
        return;
      }

      try {
        console.log('[BookingSuccessPage] Fetching bookings with orderId:', orderId);
        // Use dedicated endpoint for fetching bookings by orderId
        const response = await bookingAPI.getByOrderId(orderId);
        console.log('[BookingSuccessPage] API response:', response);
        const bookingsData = Array.isArray(response.data) ? response.data : [];

        if (bookingsData.length === 0) {
          setError(t.errors.api.BOOKING_NOT_FOUND || 'Booking not found');
          setLoading(false);
          return;
        }

        setBookings(bookingsData);
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching bookings:', err);
        if (err.response?.status === 404) {
          setError(t.errors.api.BOOKING_NOT_FOUND || 'Booking not found');
        } else {
          setError(t.errors.general);
        }
        setLoading(false);
      }
    };

    fetchBookings();
  }, [orderId, state, t]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await userAPI.getProfile();
        if (response.data.phoneNumber) {
          setUserPhone(response.data.phoneNumber);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    if (isAuthenticated) {
      fetchUserProfile();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (bookings.length > 0 && !hasTrackedPurchase.current) {
      hasTrackedPurchase.current = true;
      clearEcommerce();
      
      const items = bookings.map(booking => {
        const variant = booking.startTime 
          ? formatInTimeZone(new Date(booking.startTime), TALLINN_TIMEZONE, 'dd_MM_yyyy_HH:mm')
          : '';
        return {
          item_id: booking.serviceId,
          item_name: getTranslatedField(booking.service, 'name', 'en') || booking.service?.name,
          item_category: 'Service',
          item_variant: variant,
          location_id: 'Tallinn'
        };
      });

      const totalValue = bookings.reduce((sum, booking) => {
        const price = (booking.price ?? 0) / 100;
        return sum + price;
      }, 0);

      const firstBooking = bookings[0];
      const customerEmail = firstBooking?.guestEmail || user?.email || '';
      const customerPhone = firstBooking?.guestPhone || userPhone || '';

      pushToDataLayer('purchase', {
        content: { category: 'booking_service' },
        ecommerce: {
          currency: 'EUR',
          value: totalValue,
          transaction_id: orderId,
          items
        },
        customer: {
          email: customerEmail,
          phone: customerPhone
        }
      });
    }
  }, [bookings, orderId, language, user, userPhone, isAuthenticated]);

  const formatDateTime = (dateTimeStr: string) => {
    const date = new Date(dateTimeStr);
    const dateLocale = language === 'ru' ? ruLocale : language === 'et' ? etLocale : language === 'fi' ? fiLocale : undefined;
    return formatInTimeZone(date, TALLINN_TIMEZONE, 'EEEE, MMMM d, yyyy', { locale: dateLocale });
  };

  const formatTime = (dateTimeStr: string) => {
    const date = new Date(dateTimeStr);
    return formatInTimeZone(date, TALLINN_TIMEZONE, 'HH:mm');
  };

  const parseDuration = (duration: number | string): number => {
    if (typeof duration === 'number') {
      return duration;
    }

    const hoursMatch = duration.match(/PT(\d+)H/);
    if (hoursMatch) {
      return parseInt(hoursMatch[1]) * 60;
    }

    const minutesMatch = duration.match(/PT(\d+)M/);
    if (minutesMatch) {
      return parseInt(minutesMatch[1]);
    }

    const combinedMatch = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
    if (combinedMatch) {
      const hours = combinedMatch[1] ? parseInt(combinedMatch[1]) : 0;
      const minutes = combinedMatch[2] ? parseInt(combinedMatch[2]) : 0;
      return hours * 60 + minutes;
    }

    return 60;
  };

  if (loading) {
    return (
      <div className="py-12 max-w-2xl mx-auto px-4 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">{t.common.loading}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 max-w-2xl mx-auto px-4 text-center">
        <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h1 className="mt-4 text-2xl font-bold text-gray-900">{t.common.error}</h1>
        <p className="mt-2 text-gray-600">{error}</p>
        <div className="mt-6">
          <Link
            to="/"
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
          >
            {t.common.goHome}
          </Link>
        </div>
      </div>
    );
  }

  // Handle guest bookings display
  if (state?.guestBooking) {
    const bookingCount = state.bookingCount || 1;
    return (
      <div className="py-12 max-w-2xl mx-auto px-4">
        <div className="text-center">
          <svg className="mx-auto h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h1 className="mt-4 text-3xl font-bold text-gray-900">
            {bookingCount > 1 ? t.booking.bulkBookingSuccessTitle : t.booking.success}
          </h1>
          <p className="mt-2 text-lg text-gray-600">
            {bookingCount > 1
              ? t.booking.bulkBookingSuccess?.replace('{{count}}', bookingCount.toString()) || `Successfully created ${bookingCount} bookings!`
              : t.booking.guestBookingSuccess || 'Your booking has been confirmed. You will receive a confirmation email.'}
          </p>
        </div>

        {state.service && state.date && state.time && (
          <div className="mt-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">
                {getTranslatedField(state.service, 'name', language)}
              </h3>
              <div className="space-y-3 text-gray-700">
                <div className="flex justify-between py-2 border-b">
                  <span>{t.booking.dateLabel}</span>
                  <span className="font-medium">{formatInTimeZone(new Date(state.date), TALLINN_TIMEZONE, 'EEEE, MMMM d, yyyy', { locale: language === 'ru' ? ruLocale : language === 'et' ? etLocale : language === 'fi' ? fiLocale : undefined })}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span>{t.booking.timeLabel}</span>
                  <span className="font-medium">{state.time}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span>{t.booking.durationLabel}</span>
                  <span className="font-medium">{state.service.duration} {t.booking.minutes}</span>
                </div>
                {state.service.price && state.service.price > 0 && (
                  <div className="flex justify-between py-2">
                    <span>{t.booking.priceLabel}</span>
                    <span className="font-medium">
                      {state.service.currency === 'EUR' ? formatPrice(state.service.price) : `${state.service.currency}${state.service.price.toFixed(2)}`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 flex justify-center">
          <Link
            to="/book"
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
          >
            {t.booking.bookAnotherSession}
          </Link>
        </div>
      </div>
    );
  }

  // Handle authenticated user bookings display
  return (
    <div className="py-12 max-w-2xl mx-auto px-4">
      <div className="text-center">
        <svg className="mx-auto h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h1 className="mt-4 text-3xl font-bold text-gray-900">
          {bookings.length > 1 ? t.booking.bulkBookingSuccessTitle : t.booking.success}
        </h1>
        <p className="mt-2 text-lg text-gray-600">
          {bookings.length > 1
            ? t.booking.bulkBookingSuccess?.replace('{{count}}', bookings.length.toString()) || `Successfully created ${bookings.length} bookings!`
            : t.booking.guestBookingSuccess || 'Your booking has been confirmed. You will receive a confirmation email.'}
        </p>
      </div>

      <div className="mt-8 space-y-4">
        {bookings.map((booking) => (
          <div key={booking.id} className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">
              {getTranslatedField(booking.service, 'name', language)}
            </h3>
            <div className="space-y-3 text-gray-700">
              <div className="flex justify-between py-2 border-b">
                <span>{t.booking.dateLabel}</span>
                <span className="font-medium">{booking.startTime ? formatDateTime(booking.startTime) : '-'}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span>{t.booking.timeLabel}</span>
                <span className="font-medium">{booking.startTime ? formatTime(booking.startTime) : '-'}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span>{t.booking.durationLabel}</span>
                <span className="font-medium">{booking.duration ? `${parseDuration(booking.duration)} ${t.booking.minutes}` : '-'}</span>
              </div>
              {(booking.price ?? 0) > 0 && booking.metadata?.stripe_payment_intent_id && (
                <div className="flex justify-between py-2">
                  <span>{t.booking.priceLabel}</span>
                  <span className="font-medium">
                    {booking.currency === 'EUR' ? formatPrice((booking.price ?? 0) / 100) : `${booking.currency}${((booking.price ?? 0) / 100).toFixed(2)}`}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex justify-center space-x-4">
        {isAuthenticated && (
          <Link
            to="/account"
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
          >
            {t.account.title}
          </Link>
        )}
        <Link
          to="/book"
          className={`px-6 py-3 rounded-md font-medium ${
            isAuthenticated
              ? 'border border-gray-300 text-gray-700 hover:bg-gray-50'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {t.booking.bookAnotherSession}
        </Link>
      </div>
    </div>
  );
};

export default BookingSuccessPage;
